import { mat3, vec2 } from 'gl-matrix'
import { COLORS, TYPES } from '../const'
import TWEEN from '@tweenjs/tween.js'

export default class Shape {
	protected readonly transform: { top: number; left: number; alpha: number; blur: number }
	protected readonly resolution: [number, number] = [0, 0]
	protected readonly vec2 = vec2.create()

	protected type = TYPES.SHAPE
	protected baseAspect: number
	protected len: number
	protected program: WebGLProgram
	protected visible = true
	protected positionBuffer: WebGLBuffer
	protected sizing: string

	private readonly align: string[]
	private readonly skew: number[]
	private readonly anchor: number[]
	private readonly scale: number[]
	private readonly keyframes: any
	private readonly color: number[]
	private readonly cover: boolean
	private readonly autoResize: boolean[]
	private readonly tweens: any[] = []
	private readonly width = 0
	private readonly height = 0
	private readonly skewMatrix = mat3.create()
	private readonly matrix = mat3.create()
	private positionAttributeLocation: GLint
	private colorLocation: WebGLUniformLocation
	private matrixLocation: WebGLUniformLocation
	private slideLocation: WebGLUniformLocation
	private alphaLocation: WebGLUniformLocation

	constructor({
		cover = false,
		skew = [0, 0],
		anchor = [0, 0],
		color = COLORS.WHITE,
		scale = [1, 1],
		baseAspect = 1,
		align = ['center', 'center'],
		sizing = 'height',
		autoResize = [false, false],
		keyframes
	}) {
		this.visible = true

		this.align = align
		this.skew = skew
		this.anchor = anchor
		this.scale = scale
		this.keyframes = keyframes
		this.color = color
		this.cover = cover
		this.baseAspect = baseAspect
		this.autoResize = autoResize
		this.sizing = sizing

		this.transform = {
			alpha: 1,
			left: 0,
			top: 0,
			blur: 0
		}

		this.width = 0
		this.height = 0
		this.skewMatrix = mat3.create()
		this.matrix = mat3.create()
		this.vec2 = vec2.create()
	}

	init(gl, program) {
		this.program = program

		//prettier-ignore
		mat3.set(this.skewMatrix,
			1, this.skew[1], 0,
			this.skew[0], 1, 0,
			0, 0, 1,
		)

		this.positionAttributeLocation = gl.getAttribLocation(this.program, 'a_position')
		this.colorLocation = gl.getUniformLocation(this.program, 'u_color')
		this.matrixLocation = gl.getUniformLocation(this.program, 'u_matrix')
		this.slideLocation = gl.getUniformLocation(this.program, 'u_slide')
		this.alphaLocation = gl.getUniformLocation(this.program, 'u_alpha')

		// prettier-ignore
		const positions = [
			0, 0,
			1, 0,
			0, 1,

			0, 1,
			1, 0,
			1, 1,
		]

		this.len = positions.length / 2
		this.positionBuffer = gl.createBuffer()
		gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer)
		gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW)
	}

	resize(width, height) {
		this.resolution[0] = width
		this.resolution[1] = height

		let w = 0
		let h = 0
		let y = 0
		let x = 0

		switch (this.sizing) {
			case 'cover':
				if (width / height > this.baseAspect) {
					w = width
					h = width / this.baseAspect
				} else {
					h = height
					w = height * this.baseAspect
				}
				break
			case 'fill':
				w = width
				h = height
				w += Math.abs(h * this.skew[0])
				h += Math.abs(w * this.skew[1])
				break
			case 'width':
				w = width
				h = w * (1 / this.baseAspect)
				break
			case 'height':
				h = height
				w = h * this.baseAspect
				break
			default:
				throw new Error(`unknown sizing param: ${this.sizing}`)
		}

		x -= Math.min(this.skew[0], 0) * h
		y -= Math.min(this.skew[1], 0) * w

		w *= this.scale[0]
		h *= this.scale[1]

		switch (this.align[0]) {
			case 'center':
				x += (width - w) * 0.5
				x -= Math.abs(h * this.skew[0]) * 0.5
				break
			case 'left':
				break
			case 'right':
				x += width
				x -= w
				break
			default:
				throw new Error(`unknown align param: ${this.sizing}`)
		}

		switch (this.align[1]) {
			case 'center':
				y += (height - h) * 0.5
				y -= Math.abs(w * this.skew[1]) * 0.5
				break
			case 'top':
				break
			default:
				throw new Error(`unknown align param: ${this.sizing}`)
		}

		x += this.anchor[0] * (this.sizing === 'fill' ? width : w)
		y += this.anchor[1] * (this.sizing === 'fill' ? height : h)

		w = Math.round(w)
		h = Math.round(h)
		x = Math.round(x)
		y = Math.round(y)

		mat3.projection(this.matrix, width, height)
		mat3.multiply(this.matrix, this.matrix, this.skewMatrix)
		mat3.translate(this.matrix, this.matrix, vec2.set(this.vec2, x, y))
		mat3.scale(this.matrix, this.matrix, vec2.set(this.vec2, w, h))
	}

	setAnchor(left, top) {
		if (this.autoResize[0]) this.anchor[0] = left
		if (this.autoResize[1]) this.anchor[1] = top
	}

	draw(gl) {
		if (!this.visible) return
		if (this.transform.alpha === 0) return
		gl.useProgram(this.program)
		this.setAttributesAndUniforms(gl)
		gl.drawArrays(gl.TRIANGLES, 0, this.len)
	}

	setAttributesAndUniforms(gl) {
		gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer)
		gl.enableVertexAttribArray(this.positionAttributeLocation)
		gl.vertexAttribPointer(this.positionAttributeLocation, 2, gl.FLOAT, false, 0, 0)

		gl.uniform1f(this.alphaLocation, this.transform.alpha)
		gl.uniform2f(this.slideLocation, this.transform.left, this.transform.top)
		gl.uniformMatrix3fv(this.matrixLocation, false, this.matrix)
		gl.uniform4fv(this.colorLocation, this.color)
	}

	async animateIn(forward, autoVisibility = true) {
		if (autoVisibility) this.visible = true
		const keyframes = this.keyframes ? (forward ? this.keyframes.inForward : this.keyframes.inBackward) : null
		if (keyframes) await this.animate(keyframes)
	}

	async animateOut(forward, autoVisibility = true) {
		const keyframes = this.keyframes ? (forward ? this.keyframes.outForward : this.keyframes.outBackward) : null
		if (keyframes) await this.animate(keyframes)
		if (autoVisibility) this.visible = false
	}

	async animate({
		easing = TWEEN.Easing.Linear.None,
		left = () => [0, 0],
		top = () => [0, 0],
		alpha = [1, 1],
		blur = [0, 0],
		duration = 1500,
		delay = 0
	}) {
		this.tweens.forEach((tween) => tween.stop())
		this.tweens.length = 0

		const x = left()
		const y = top()

		this.transform.left = x[0] * 2
		this.transform.top = y[0] * 2
		this.transform.alpha = alpha[0]
		this.transform.blur = blur[0]

		const to = {
			left: x[1] * 2,
			top: y[1] * 2,
			alpha: alpha[1],
			blur: blur[1]
		}

		return new Promise((resolve) => {
			this.tweens.push(
				new TWEEN.Tween(this.transform)
					.to(to, typeof duration === 'number' ? duration : 1500)
					.delay(typeof delay === 'number' ? delay : 0)
					.easing(typeof easing === 'function' ? easing : TWEEN.Easing.Linear.None)
					.onUpdate(() => {})
					.onComplete(resolve)
					.start()
			)
		})
	}

	preload() {
		return Promise.resolve()
	}
}
